<template lang="pug">
  .chart
    h3.title-medium.m-bottom-3
      | {{ $t('stats.abilityDevelopment.title') }}
    .chart-card
      .relative
        loader.loader.absolute(v-if='isLoading' size='inner' whiteBackground)
        .card-info
          .row
            .abilities-container
              h3.text-normal.m-bottom-2.bold.abilities-title
                | {{ $t('stats.abilityDevelopment.allAbilities') }}
              p.ability.text-xsmall.dark-gray(v-for='(ability, index) in abilities' :key='index' :style='getAbilityStyles(ability.color)')
                | {{ $t(ability.label) }}
            .column.chart-container
              .row.chart-header
                .title-container.row(v-for='categoryData in categoriesData' :key='categoryData.category.id' :style='setCategoryColor(categoryData.category.id)')
                  img.m-right-1.image(:src='setCategoryImage(categoryData.category.imageURL)' :alt='categoryData.category.name')
                  span.title
                    | {{ categoryData.category.name }}
              .chart(id='chart')
      .card-footer
        .column.m-right-2
          span.text-xsmall.dark-gray
            | {{ $t('stats.chartOptions.time') }}
          k-select.filter-content.m-top-1(
            name='startingDate'
            :options='getTranslatedOptions(timeRangesOptions)'
            keyName='value'
            :preselectedOption='filters.startingDate'
            @selectOption='updateFilter'
            noSpacing
          )
        .column(v-if='isAdminUser || isTrial')
          span.text-xsmall.dark-gray
            | {{ $t('stats.chartOptions.school') }}
          k-select.filter-content.m-top-1(
            name='schoolId'
            :options='schools'
            keyName='value'
            :emptyOption='$t("stats.allSchools")'
            noSpacing
            @selectOption='updateFilter'
          )
</template>

<script>
import * as am4core from '@amcharts/amcharts4/core'
import * as am4charts from '@amcharts/amcharts4/charts'
import { mapActions, mapState } from 'vuex'
import amplitude from 'amplitude-js'

import KSelect from '@/components/KSelect'
import Loader from '@/components/Loader'
import stats from '../../mixins/stats'
import userRole from '../../mixins/userRole'

import setStackedChartConfig from '../../config/charts/stackChart'
import { abilities, categoriesColors } from '../../utils/constants'

export default {
  mixins: [stats, userRole],
  data() {
    return {
      filters: {
        schoolId: null,
        startingDate: 1
      },
      abilities
    }
  },
  created() {
    this.updateChart()
  },
  computed: {
    ...mapState({
      categoriesData: state => state.statistics.skillsPerCategory
    }),
    ...mapState({
      isLoading: state => state.statistics.skillsPerCategoryLoading
    })
  },
  methods: {
    ...mapActions(['getSkillsPerCategory']),
    async updateChart() {
      amplitude.getInstance().logEvent('Filter Statistics', {
        chartName: 'Ability development',
        filters: this.filters
      })
      await this.getSkillsPerCategory(this.filters)
      const chart = am4core.create('chart', am4charts.XYChart)
      setStackedChartConfig(chart, this.categoriesData)
    },
    getAbilityStyles(color) {
      return {
        '--ability-color': color
      }
    },
    setCategoryColor(categoryId) {
      return {
        '--category-color': categoriesColors[categoryId].color
      }
    },
    setCategoryImage(imageName) {
      return imageName
    }
  },
  components: { KSelect, Loader }
}
</script>

<style lang="scss" scoped>
@import '../../scss/variables/colors';

.abilities-title {
  max-width: 100px;
}

.chart-card {
  background-color: $white;
  border: 1px solid $light-gray;
  box-shadow: 0 2px 8px 0 $shadow-gray;
  border-radius: 10px;
  width: 750px;
}

.card-info {
  display: flex;
  padding: 45px 30px 15px;
}

.chart-icon {
  height: 305px;
  width: 515px;
}

.detailed-chart-icon {
  height: 305px;
  width: 470px;
}

.card-footer {
  align-items: center;
  border-top: 1px solid $light-gray;
  display: flex;
  height: 80px;
  padding: 10px 30px;
}

.filter-content {
  min-width: 180px;
}

.abilities-container {
  display: flex;
  flex-direction: column;
  min-width: 160px;

  .ability {
    align-items: baseline;
    display: flex;
    font-size: 10px;
    line-height: 12px;
    margin-bottom: 12px;

    &::before {
      background-color: var(--ability-color);
      border-radius: 100%;
      content: '';
      height: 7px;
      margin-right: 10px;
      position: relative;
      width: 7px;
    }
  }
}

.chart-container {
  $chart-value-label-width: 46px;
  $chart-columns-separation: 8px;
  $chart-margins: 7px;

  min-width: 550px;

  .chart-header {
    padding-right: calc(#{$chart-value-label-width} + #{$chart-columns-separation} + #{$chart-margins});
    padding-left: calc(#{$chart-columns-separation} + #{$chart-margins});

    .title-container {
      border-bottom: 2px solid var(--category-color);
      width: 90%;
      padding: 0 0 8px 0;
      margin: 0 $chart-columns-separation;

      .image {
        height: 25px;
      }

      .title {
        font-size: 12px;
        line-height: 15px;
        max-width: 90px;
        min-height: 30px;
        color: $dark-gray;
      }
    }
  }

  .chart {
    min-height: 300px
  }
}
</style>
